import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { EventsAPI } from "@/admin/api";
import { useAdmin } from "@/admin/store";
import { BtnGhost, BtnPrimary, Confirm, Field, Modal, PageHeader, SearchInput, inputCls } from "@/components/admin/ui";

const EventTags = () => {
  const { log } = useAdmin();
  const queryClient = useQueryClient();
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);

  const [q, setQ] = useState("");

  const { data: eventTags = [], isLoading } = useQuery({
    queryKey: ["eventTags"],
    queryFn: EventsAPI.getTags,
  });

  const createMutation = useMutation({
    mutationFn: EventsAPI.createTag,
    onSuccess: (data) => {
      queryClient.invalidateQueries(["eventTags"]);
      log("Event tag added", data?.tagName || newName.trim());
      setNewName("");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => EventsAPI.updateTag(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(["eventTags"]);
      log("Event tag updated", variables.payload.tagName);
      setEditing(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: EventsAPI.deleteTag,
    onSuccess: (_, id) => {
      queryClient.invalidateQueries(["eventTags"]);
      setConfirmDel(null);
    },
  });

  const addTag = () => {
    if (!newName.trim()) return;
    createMutation.mutate({ tagName: newName.trim() });
  };

  const rows = eventTags.filter((t) => (t.tagName || "").toLowerCase().includes(q.toLowerCase()));

  if (isLoading) return <div className="p-8 text-cream/50">Loading event tags...</div>;

  return (
    <div data-testid="admin-event-tags">
      <PageHeader title="EVENT TAGS" sub="The labels we give our gatherings.">
        <SearchInput value={q} onChange={setQ} placeholder="Search tags" />
      </PageHeader>
      <div className="mb-8 flex flex-wrap items-center gap-3">
        <input
          data-testid="event-tag-new-input"
          className={inputCls + " max-w-xs"}
          placeholder="A new tag, e.g. Harvest"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addTag()}
          disabled={createMutation.isPending}
        />
        <BtnPrimary data-testid="event-tag-add-btn" onClick={addTag} disabled={createMutation.isPending}>
          + Add
        </BtnPrimary>
      </div>

      <div className="border-t border-cream/10">
        {rows.map((t) => (
          <div key={t.tagId} data-testid={`event-tag-row-${t.tagId}`} className="flex items-center justify-between gap-4 border-b border-cream/10 py-4">
            <div className="flex items-center gap-5">
              <span className="font-display text-lg text-cream">{t.tagName}</span>
            </div>
            <div className="flex gap-3">
              <button data-testid={`event-tag-edit-${t.tagId}`} onClick={() => { setName(t.tagName); setEditing(t.tagId); }} className="font-body text-[10px] tracking-[0.2em] uppercase text-brass/80 hover:text-brass">Edit</button>
              <button data-testid={`event-tag-delete-${t.tagId}`} onClick={() => setConfirmDel(t)} className="font-body text-[10px] tracking-[0.2em] uppercase text-terra/80 hover:text-terra">Delete</button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <Modal title="EDIT TAG" onClose={() => setEditing(null)}>
          <Field label="Tag Name">
            <input data-testid="event-tag-form-name" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <div className="mt-8 flex justify-end gap-3">
            <BtnGhost onClick={() => setEditing(null)}>Cancel</BtnGhost>
            <BtnPrimary data-testid="event-tag-form-save" onClick={() => updateMutation.mutate({ id: editing, payload: { tagName: name.trim() } })} disabled={updateMutation.isPending}>
              Save Changes
            </BtnPrimary>
          </div>
        </Modal>
      )}
      {confirmDel && (
        <Confirm
          testId="event-tag-delete-confirm"
          title="REMOVE THIS TAG?"
          body={`“${confirmDel.tagName}” will be lifted from the events it marks.`}
          onCancel={() => setConfirmDel(null)}
          onConfirm={() => {
            deleteMutation.mutate(confirmDel.tagId);
            log("Event tag removed", confirmDel.tagName);
          }}
        />
      )}
    </div>
  );
};

export default EventTags;
